import type { Coder } from './coder.mjs';
import { getCaretFlatOffset, setCaretFromFlatOffset } from './coder.helpers.mjs';
import EventManager from '../eventManager.mjs';
import { SPACER } from '../helpers/iml.mjs';

export const UNDO_HISTORY_MAX_SIZE = 50;
// Node classes that can be dynamically added but functionally do not matter here.
// As they could break the comparison of nodes, or they could result in reverting to a broken state,
// it is better to remove them.
const TRANSIENT_HISTORY_CLASSES = ['hover', 'selection', 'top-gap', 'bottom-gap'];

export type CoderHistoryState = { stateNodes: Node[]; caret: number; semanticKey: string };

export class CoderHistory {
	private history: CoderHistoryState[] = [];
	private historyIndex = 0;
	private ignoreNext = false;

	private observer?: MutationObserver;
	private abortController?: AbortController;

	constructor(private readonly coder: Coder) {
		this.listenForInitSnapshot();
	}

	undo(event?: Event) {
		if (this.isEditingBlocked()) return;

		event?.preventDefault();

		if (this.history.length === 0) return;

		if (this.historyIndex <= 0) {
			this.historyIndex = 0;
			return;
		}

		this.ignoreNext = true;
		this.historyIndex--;

		this.setStateAndDispatchEvent();
	}

	redo(event?: Event) {
		if (this.isEditingBlocked()) return;

		event?.preventDefault();

		if (this.history.length === 0) return;

		if (this.historyIndex >= this.history.length - 1) {
			this.historyIndex = this.history.length - 1;
			return;
		}

		this.ignoreNext = true;
		this.historyIndex++;

		this.setStateAndDispatchEvent();
	}

	connect() {
		this.disconnect();

		this.observer = new MutationObserver(() => {
			this.onChange();
		});

		this.observer.observe(this.coder, {
			attributes: false,
			childList: true,
			subtree: true,
			characterData: true,
			characterDataOldValue: false,
		});

		this.abortController?.abort();

		this.abortController = new AbortController();
		document.addEventListener('selectionchange', () => this.updateCaretPosition(), {
			capture: true,
			signal: this.abortController!.signal,
		});
	}

	disconnect() {
		this.observer?.disconnect();
		this.abortController?.abort();
		this.clearHistory();
	}

	private onChange() {
		if (this.ignoreNext) {
			this.ignoreNext = false;
			return;
		}

		if (this.hasProhibitedNodes()) {
			return;
		}

		const lastState = this.history[this.historyIndex];

		const caret: number = getCaretFlatOffset(this.coder);
		const stateNodes = [...this.coder.childNodes].map((child) => this.cloneNodeForHistory(child));
		const semanticKey: string = this.coder.value + `[${this.getRelevantNodeCount(stateNodes)}]`;

		if (lastState && lastState.semanticKey === semanticKey) {
			// Update caret of the last state
			lastState.caret = caret;
			return;
		}

		this.history = this.history.slice(0, this.historyIndex + 1);
		this.history.push({ stateNodes, caret, semanticKey });

		while (this.history.length > UNDO_HISTORY_MAX_SIZE) {
			this.history.shift();
		}

		this.historyIndex = this.history.length - 1;
	}

	private setStateByHistoryIndex() {
		const state = this.history.at(this.historyIndex);

		if (!state) {
			// Undo / redo did not happen, reset ignoreNext
			this.ignoreNext = false;
			return;
		}

		// Clean up the element
		this.coder.innerHTML = '';

		for (const clone of state.stateNodes) {
			// Clone node to prevent mutation of history state
			this.coder.append(clone.cloneNode(true));
		}

		setCaretFromFlatOffset(this.coder, state.caret);
	}

	private isEditingBlocked() {
		return this.coder.hasAttribute('readonly') || this.coder.hasAttribute('disabled');
	}

	private listenForInitSnapshot() {
		const controller = new AbortController();

		const initSnapshotHandler = () => {
			this.onChange();

			controller.abort();
		};

		const keydownInitSnapshotHandler = (event: KeyboardEvent) => {
			if (['Backspace', 'Delete'].includes(event.key)) {
				initSnapshotHandler();
			}
		};

		['beforeinput', 'paste', 'cut', 'drop'].forEach((ev) =>
			this.coder.addEventListener(ev, initSnapshotHandler, {
				once: true,
				capture: true,
				signal: controller.signal,
			}),
		);

		this.coder.addEventListener('keydown', keydownInitSnapshotHandler, {
			signal: controller.signal,
		});
	}

	private updateCaretPosition() {
		const selection = document.getSelection();
		const anchorNode = selection?.anchorNode;

		if (!anchorNode || !this.coder.contains(anchorNode)) return;

		const caret = getCaretFlatOffset(this.coder);

		if (caret === -1) return;

		const historyItem = this.history[this.historyIndex];

		if (!historyItem) return;

		historyItem.caret = caret;
	}

	private setStateAndDispatchEvent() {
		this.setStateByHistoryIndex();
		EventManager.dispatch(this.coder, 'input', { bubbles: true } as never);
	}

	private clearHistory() {
		this.history = [];
		this.historyIndex = 0;
		this.ignoreNext = false;
	}

	private cloneNodeForHistory(node: Node) {
		const clone = node.cloneNode(true);

		this.stripTransientHistoryState(clone);

		return clone;
	}

	private stripTransientHistoryState(node: Node) {
		if (!(node instanceof Element)) return;

		node.classList.remove(...TRANSIENT_HISTORY_CLASSES);

		if (!node.classList.length) {
			node.removeAttribute('class');
		}

		for (const child of node.childNodes) {
			this.stripTransientHistoryState(child);
		}
	}

	private getRelevantNodeCount(stateNodes: Node[]): number {
		let count = 0;

		for (const child of stateNodes) {
			if (child.nodeType === Node.TEXT_NODE) {
				if (child.textContent === SPACER) continue;
			}

			count++;
		}

		return count;
	}

	private hasProhibitedNodes(): boolean {
		return !!this.coder.querySelector('imt-coder-token');
	}
}
